import { inject, Injectable, signal } from '@angular/core';
import { MetalConfigModel } from '../games/metal/models/config.model';
import { MetalGlobalModel } from '../games/metal/models/global.model';
import { MetalLocalModel } from '../games/metal/models/local.model';
import { TidalConfigModel } from '../games/tidal/models/config.model';
import { TidalGlobalModel } from '../games/tidal/models/global.model';
import { TidalLocalModel } from '../games/tidal/models/local.model';
import { CycleConfigModel } from '../tools/cycle/models/config.model';
import { CycleGlobalModel } from '../tools/cycle/models/global.model';
import { CycleLocalModel } from '../tools/cycle/models/local.model';
import { DicerConfigModel } from '../tools/dicer/models/config.model';
import { DicerGlobalModel } from '../tools/dicer/models/global.model';
import { DicerLocalModel } from '../tools/dicer/models/local.model';
import { ChainHelper } from './../helpers/chain.helper';
import { WalletHelper } from './../helpers/wallet.helper';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AlphaHelper {

  /**
   * Chain helper
   */
  chain_helper = inject(ChainHelper);

  /**
   * Wallet helper
   */
  wallet_helper = inject(WalletHelper);

  /**
   * Metal configuration
   */
  readonly metal_config = signal(new MetalConfigModel());

  /**
   * Metal global details
   */
  readonly metal_global = signal(new MetalGlobalModel());

  /**
   * Metal local details
   */
  readonly metal_local = signal(new MetalLocalModel());

  /**
   * Tidal configuration
   */
  readonly tidal_config = signal(new TidalConfigModel());

  /**
   * Tidal global details
   */
  readonly tidal_global = signal(new TidalGlobalModel());

  /**
   * Tidal local details
   */
  readonly tidal_local = signal(new TidalLocalModel());

  /**
   * Cycle configuration
   */
  readonly cycle_config = signal(new CycleConfigModel());

  /**
   * Cycle global details
   */
  readonly cycle_global = signal(new CycleGlobalModel());

  /**
   * Cycle local details
   */
  readonly cycle_local = signal(new CycleLocalModel());

  /**
   * Dicer configuration
   */
  readonly dicer_config = signal(new DicerConfigModel());

  /**
   * Dicer global details
   */
  readonly dicer_global = signal(new DicerGlobalModel());

  /**
   * Dicer local details
   */
  readonly dicer_local = signal(new DicerLocalModel());

  /**
   * Read details task
   */
  private read_details_task: any;

  /**
   * Start tasks
   */
  startTasks() {
    this.read_details_task = setInterval(() => { this.readDetails() }, 3000);
  }

  /**
   * Stop tasks
   */
  stopTasks() {
    if (this.read_details_task) {
      clearInterval(this.read_details_task);
    }
  }

  /**
   * Read details
   */
  private async readDetails() {
    this.readGlobalDetails();
    this.readLocalDetails();
  }

  /**
   * Read global details
   */
  private async readGlobalDetails() {
    // Define values
    let metal_global = new MetalGlobalModel();
    let tidal_global = new TidalGlobalModel();
    let cycle_global = new CycleGlobalModel();
    let dicer_global = new DicerGlobalModel();

    // Read properties
    let data = await this.chain_helper.readObject(environment.config.alpha.state_storage_id);
    if (data) {
      let contents = data.global?.contents;
      if (contents) {
        metal_global.fill(contents);
        tidal_global.fill(contents);
        cycle_global.fill(contents);
        dicer_global.fill(contents);
      }
    }

    this.metal_global.set(metal_global);
    this.tidal_global.set(tidal_global);
    this.cycle_global.set(cycle_global);
    this.dicer_global.set(dicer_global);
  }

  /**
   * Read local details
   */
  private async readLocalDetails() {
    // Define values
    let metal_local = new MetalLocalModel();
    let tidal_local = new TidalLocalModel();
    let cycle_local = new CycleLocalModel();
    let dicer_local = new DicerLocalModel();

    // Read properties
    let address = this.wallet_helper.active_account()?.address;
    if (address) {
      let data = await this.chain_helper.readDynamicField(environment.config.alpha.state_storage_local_id, 'address', address);
      if (data) {
        let contents = data?.contents;
        if (contents) {
          metal_local.fill(contents);
          tidal_local.fill(contents);
          cycle_local.fill(contents);
          dicer_local.fill(contents);
        }
      }
    }

    this.metal_local.set(metal_local);
    this.tidal_local.set(tidal_local);
    this.cycle_local.set(cycle_local);
    this.dicer_local.set(dicer_local);
  }
}
